"use client";

import * as React from "react";
import { PhoneCall, CircleQuestionMark as HelpCircle, Clock, ShieldCheck, ArrowRight, TriangleAlert as AlertTriangle, Droplets, Wrench, SearchX as SearchAlert, Flame, MapPin, CalendarCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const faqItems = [{
  id: "item-1",
  icon: Wrench,
  question: "What plumbing repair services do you handle in Jonesboro?",
  answer: "We handle complete residential and commercial plumbing repairs. This includes burst pipe repair, sewer line backups, whole-home repiping, tank and tankless water heater troubleshooting, clogged main lines, garbage disposal failures, leaky faucets, and running or backed-up toilets."
}, {
  id: "item-2",
  icon: AlertTriangle,
  question: "How do I know if my issue is an emergency or routine service?",
  answer: "A plumbing issue is an active emergency if it risks property flooding, stops your main water supply, creates raw sewage backups, or involves a gas leak related to a water heater. Routine issues like slow-dripping faucets or planned fixture installations can be scheduled during normal business hours."
}, {
  id: "item-3",
  icon: Droplets,
  question: "Why are multiple drains backing up at the same time?",
  answer: "When multiple sinks, tubs, or toilets back up simultaneously, the issue is almost always located in your primary sewer lateral or main drain stack rather than an individual fixture pipe. We recommend shutting off water fixtures immediately and calling our emergency team for video camera inspection and hydro-jetting."
}, {
  id: "item-4",
  icon: SearchAlert,
  question: "What causes sudden low water pressure throughout the house?",
  answer: "Sudden pressure drops are commonly caused by a municipal supply outage, a failing main pressure reducing valve (PRV), a hidden slab or yard pipe break, or heavy mineral scale accumulation in older galvanized pipes. We test static pressure at the main supply to pinpoint the exact failure point."
}, {
  id: "item-5",
  icon: Flame,
  question: "Why did my water heater suddenly stop producing hot water?",
  answer: "Electric units frequently suffer from tripped circuit breakers, failed upper heating elements, or broken thermostats. Gas water heaters may have extinguished pilot lights, faulty thermocouples, or gas control valve malfunctions. If your tank is leaking water around the base, the inner vessel has likely failed and requires replacement."
}, {
  id: "item-6",
  icon: Droplets,
  question: "What are the common warning signs of a hidden water leak?",
  answer: "Key signs include an unexplained spike in your monthly water bill, warm spots on tile or concrete floors, musty mildew odors, damp baseboards, bubbling drywall paint, and the sound of running water when all taps are fully closed."
}, {
  id: "item-7",
  icon: CalendarCheck,
  question: "What should I do immediately before the plumber arrives?",
  answer: "First, locate and turn off your main water shut-off valve to prevent further flooding. If the leak is near electrical outlets or appliances, turn off the relevant circuit breakers. Move rugs, furniture, and valuables away from standing water, and avoid using chemical drain cleaners before our technicians arrive."
}, {
  id: "item-8",
  icon: MapPin,
  question: "What areas do your emergency plumbing technicians serve?",
  answer: "Our dispatch hub covers Jonesboro, Clayton County, and neighboring South Atlanta communities. We maintain fully stocked service trucks ready 24/7 to deliver fast local response times for urgent residential and commercial service calls."
}];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="faq" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="flex justify-center mb-4">
            <Badge variant="outline" className="bg-secondary/60 text-secondary-foreground border-border px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-full flex items-center gap-1.5">
              <HelpCircle className="size-3.5 text-primary" />
              <span>Got Questions? We Have Answers</span>
            </Badge>
          </motion.div>

          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Frequently Asked Plumbing Questions
          </motion.h2>

          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2,
          ease: "easeOut"
        }} className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Get clear, honest guidance on troubleshooting urgent leaks, sewer line concerns, water heater repairs, and our 24/7 service response in Jonesboro.
          </motion.p>
        </div>

        {/* Accordion List */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2,
        ease: "easeOut"
      }} className="bg-card border border-border rounded-xl shadow-sm p-4 sm:p-6 md:p-8">
          <Accordion type="single" collapsible className="w-full divide-y divide-border">
            {faqItems.map((item, index) => {
            const IconComponent = item.icon;
            return <AccordionItem key={item.id} value={item.id} data-index={index} className="border-border py-1 first:pt-0 last:pb-0">
                  <AccordionTrigger className="text-left font-default font-semibold text-base sm:text-lg text-foreground hover:no-underline hover:text-primary py-4 group">
                    <div className="flex items-start gap-3 sm:gap-4 pr-2">
                      <div className="mt-0.5 rounded-lg bg-secondary text-primary p-2 shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                        <IconComponent className="size-4" />
                      </div>
                      <span className="leading-snug">{item.question}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pl-11 sm:pl-12 pr-4 pt-1 pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>;
          })}
          </Accordion>
        </motion.div>

        {/* Quick Contact & Emergency Assistance Banner */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.3,
        ease: "easeOut"
      }} className="mt-12">
          <Card className="bg-primary text-primary-foreground border-none rounded-2xl shadow-md overflow-hidden relative">
            <CardContent className="p-6 sm:p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 relative z-10">
              <div className="space-y-2 text-center md:text-left">
                <div className="inline-flex items-center gap-2 bg-primary-foreground/10 px-3 py-1 rounded-full text-xs font-semibold text-primary-foreground">
                  <Clock className="size-3.5 text-accent" />
                  <span>24/7 Rapid Dispatch Available</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-default font-bold tracking-tight text-primary-foreground">
                  Still Have an Urgent Plumbing Issue?
                </h3>
                <p className="text-primary-foreground/80 text-sm sm:text-base max-w-xl">
                  Our licensed technicians in Jonesboro are standing by to stop active leaks, unclog critical drains, and restore your hot water fast.
                </p>
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 pt-2 text-xs sm:text-sm text-primary-foreground/90">
                  <span className="flex items-center gap-1.5">
                    <ShieldCheck className="size-4 text-accent" />
                    Licensed & Insured
                  </span>
                  <span className="flex items-center gap-1.5">
                    <MapPin className="size-4 text-accent" />
                    Clayton County Local
                  </span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
                <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shadow-sm w-full sm:w-auto">
                  <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                    <PhoneCall className="size-4" />
                    <span>Call (470) 847-9076</span>
                  </a>
                </Button>

                <Button asChild variant="outline" size="lg" className="bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20 hover:bg-primary-foreground/20 hover:text-primary-foreground font-semibold w-full sm:w-auto">
                  <Link to="/contact/" className="flex items-center justify-center gap-2">
                    <span>Request Service</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}